/**
 * Concorrentes no Mercado Livre: cole o link de um anúncio do concorrente e a Berytus acompanha todo dia os anúncios
 * e preços desse vendedor, lado a lado com os seus (pelo produto de catálogo e pelo EAN).
 */
import { useState, type FormEvent } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Download, ExternalLink, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { api, brl, dateTime, int, pct } from '../api.ts';
import { ErrorBox, Loading, Tag, Thumb } from '../components/ui.tsx';
import { downloadCsv } from '../csv.ts';
import { Head, Pager, useSearchBox, useUrlState } from './MeliChannel.tsx';
import { BuyBoxPanel } from './BuyBox.tsx';

interface Competitor {
  id: string; sellerId: number; nickname: string | null; permalink: string | null; reputation: string | null; powerSeller: string | null;
  status: 'queued' | 'running' | 'idle' | 'failed'; method: 'search' | 'catalog' | null; totalItems: number | null; itemsRead: number | null;
  coverageNote: string | null; lastError: string | null; progress: { phase?: string; read?: number; total?: number | null } | null;
  refreshedAt: string | null; activeItems: number; common: number; cheaper: number; pricier: number; avgDiff: number | null; fullItems: number; changed7d: number;
}
interface Item {
  itemId: string; title: string; price: number | null; originalPrice: number | null; prevPrice: number | null; priceChangedAt: string | null;
  catalogProductId: string | null; catalogListing: boolean; listingType: string | null; full: boolean; freeShipping: boolean | null;
  brand: string | null; gtin: string | null; permalink: string | null; thumbnail: string | null;
  myItem: string | null; myPrice: number | null; myTitle: string | null; myPermalink: string | null; matchBy: 'catalog' | 'gtin' | null; diff: number | null;
}
interface Choice { sellerId: number; nickname: string | null; itemId: string; price: number | null; full: boolean }
type View = 'all' | 'common' | 'cheaper' | 'pricier' | 'equal' | 'only_them' | 'changed';
interface ItemsPage { counts: Record<View, number>; total: number; page: number; pageSize: number; rows: Item[] }

const VIEWS: { key: View; label: string; note: string }[] = [
  { key: 'cheaper', label: 'Ele está mais barato', note: 'produtos em comum' },
  { key: 'pricier', label: 'Ele está mais caro', note: 'produtos em comum' },
  { key: 'equal', label: 'Mesmo preço', note: 'produtos em comum' },
  { key: 'only_them', label: 'Só ele vende', note: 'você não anuncia' },
  { key: 'changed', label: 'Mudou o preço', note: 'nos últimos 7 dias' },
  { key: 'all', label: 'Todos os anúncios', note: 'ativos' },
];
const PHASE: Record<string, string> = { anuncios: 'lendo anúncios', detalhes: 'lendo EAN e frete', gravando: 'gravando', fim: 'concluindo' };
const POWER: Record<string, string> = { platinum: 'MercadoLíder Platinum', gold: 'MercadoLíder Gold', silver: 'MercadoLíder' };

const COLS: [string, number, string?][] = [['', 56], ['Anúncio do concorrente', 0], ['Preço dele', 130, 'num'], ['Meu anúncio', 260], ['Meu preço', 110, 'num'], ['Diferença', 118, 'num']];

const busyOf = (c: Competitor) => c.status === 'queued' || c.status === 'running';

export function Competitors() {
  const [params, set] = useUrlState();
  const qc = useQueryClient();
  const tab = params.get('tab') === 'concorrentes' ? 'concorrentes' : 'buybox';
  const follow = useMutation({
    mutationFn: (sellerId: number) => api<{ id: string }>('/channels/mercadolivre/competitors', { method: 'POST', json: { ref: String(sellerId), sellerId } }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['meli'] });
      set({ tab: 'concorrentes', c: d.id, q: null, page: null, bb: null, seller: null });
    },
  });
  return (
    <>
      <Head title="Buy Box e concorrentes">
        <p>
          Buy Box: a situação de cada anúncio de catálogo, quanto custa ganhar e quem ganha de você. Concorrentes: os vendedores
          que você acompanha, com os anúncios e preços deles lado a lado com os seus.
        </p>
      </Head>
      <div className="stack-y">
        <div className="seg" role="group" aria-label="Área">
          <button type="button" aria-pressed={tab === 'buybox'}
            onClick={() => set({ tab: null, q: null, page: null, view: null, c: null, sort: null })}>Buy Box</button>
          <button type="button" aria-pressed={tab === 'concorrentes'}
            onClick={() => set({ tab: 'concorrentes', q: null, page: null, bb: null, seller: null, sort: null, department: null, category: null, subcategory: null, brand: null, attrs: null })}>Concorrentes</button>
        </div>
        {follow.error && <ErrorBox error={follow.error} />}
        {tab === 'buybox' ? <BuyBoxPanel params={params} set={set} onFollow={(id) => follow.mutate(id)} /> : <CompetitorsTab params={params} set={set} />}
      </div>
    </>
  );
}

function CompetitorsTab({ params, set }: { params: URLSearchParams; set: (p: Record<string, string | null>) => void }) {
  const [search, setSearch] = useSearchBox(params, set);
  const qc = useQueryClient();
  const [ref, setRef] = useState('');

  const list = useQuery({
    queryKey: ['meli', 'competitors'],
    queryFn: () => api<Competitor[]>('/channels/mercadolivre/competitors'),
    refetchInterval: (q) => (q.state.data?.some(busyOf) ? 3000 : false),
  });
  const comps = list.data ?? [];
  const current = comps.find((c) => c.id === params.get('c')) ?? comps[0] ?? null;

  const [choices, setChoices] = useState<{ productId: string; list: Choice[] } | null>(null);
  const add = useMutation({
    mutationFn: (b: { ref: string; sellerId?: number }) =>
      api<{ id: string } | { choices: Choice[]; productId: string }>('/channels/mercadolivre/competitors', { method: 'POST', json: b }),
    onSuccess: (d) => {
      if ('choices' in d) { setChoices({ productId: d.productId, list: d.choices }); return; }
      setRef(''); setChoices(null);
      set({ c: d.id, view: null, page: null });
      qc.invalidateQueries({ queryKey: ['meli', 'competitors'] });
    },
  });
  const refresh = useMutation({
    mutationFn: (id: string) => api(`/channels/mercadolivre/competitors/${id}/refresh`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meli', 'competitors'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/channels/mercadolivre/competitors/${id}`, { method: 'DELETE' }),
    onSuccess: () => { set({ c: null, view: null, page: null }); qc.invalidateQueries({ queryKey: ['meli', 'competitors'] }); },
  });

  const onAdd = (e: FormEvent) => { e.preventDefault(); setChoices(null); if (ref.trim()) add.mutate({ ref: ref.trim() }); };

  return (
    <>
      <div className="stack-y">
        <p className="small muted" style={{ margin: 0 }}>
          Informe o ID do vendedor concorrente no Mercado Livre (ou o link da loja dele). A Berytus tenta ler os anúncios e preços
          dele todo dia às 7h; quando o Mercado Livre não libera a lista, compara pelos produtos de catálogo em disputa.
        </p>
        <form className="panel" onSubmit={onAdd} style={{ padding: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="input" style={{ flex: '1 1 320px' }} placeholder="ID do vendedor no Mercado Livre (ex.: 123456789), link da loja ou link de um anúncio dele"
            value={ref} onChange={(e) => setRef(e.target.value)} aria-label="Anúncio do concorrente" />
          <button className="btn primary" type="submit" disabled={add.isPending || !ref.trim()}><Plus size={15} /> {add.isPending ? 'Procurando…' : 'Adicionar concorrente'}</button>
          {add.error && <div style={{ flexBasis: '100%' }}><ErrorBox error={add.error} /></div>}
          {choices && (
            <div style={{ flexBasis: '100%', display: 'grid', gap: 8 }}>
              <div className="small muted">
                {choices.list.length} vendedores vendem esse produto ({choices.productId}). Escolha o concorrente:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {choices.list.map((c) => (
                  <button key={c.sellerId} type="button" className="btn sm" disabled={add.isPending} onClick={() => add.mutate({ ref: ref.trim(), sellerId: c.sellerId })}>
                    {c.nickname ?? `Vendedor ${c.sellerId}`} · {brl(c.price)}{c.full ? ' · Full' : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>

        {list.isPending ? <Loading /> : list.error ? <ErrorBox error={list.error} /> : comps.length === 0 ? (
          <div className="panel empty"><b>Nenhum concorrente ainda</b>Abra um anúncio do concorrente no Mercado Livre, copie o link e cole acima.</div>
        ) : (
          <>
            <div className="kpis" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
              {comps.map((c) => (
                <button key={c.id} type="button" className={`kpi${current?.id === c.id ? ' active' : ''}`} style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
                  onClick={() => set({ c: c.id, view: null, page: null, q: null })}>
                  <span className="k-label">
                    <b style={{ color: 'var(--text)' }}>{c.nickname ?? `Vendedor ${c.sellerId}`}</b>
                    {' '}{c.status === 'failed' ? <Tag tone="err">falhou</Tag> : busyOf(c) ? <Tag tone="info">lendo</Tag> : c.powerSeller ? <Tag tone="ok">{POWER[c.powerSeller] ?? c.powerSeller}</Tag> : null}
                  </span>
                  <span className="k-value">{int(c.activeItems)}<span className="small muted" style={{ fontSize: 13, fontWeight: 400 }}> anúncios{c.totalItems != null && c.totalItems > c.activeItems ? ` de ${int(c.totalItems)}` : ''}</span></span>
                  <span className="k-note">
                    {int(c.common)} em comum · <span style={{ color: c.cheaper ? 'var(--err)' : undefined }}>{int(c.cheaper)} mais barato que você</span>
                    {c.avgDiff != null && <> · média {c.avgDiff > 0 ? '+' : ''}{pct(c.avgDiff)}</>}
                  </span>
                </button>
              ))}
            </div>
            {current && <Detail key={current.id} c={current} params={params} set={set} search={search} setSearch={setSearch}
              onRefresh={() => refresh.mutate(current.id)} refreshing={refresh.isPending}
              onRemove={() => { if (window.confirm(`Parar de acompanhar ${current.nickname ?? 'este concorrente'}? O histórico de preços dele será apagado.`)) remove.mutate(current.id); }} />}
            {(refresh.error || remove.error) && <ErrorBox error={refresh.error ?? remove.error} />}
          </>
        )}
      </div>
    </>
  );
}

function Detail({ c, params, set, search, setSearch, onRefresh, refreshing, onRemove }: {
  c: Competitor; params: URLSearchParams; set: (p: Record<string, string | null>) => void; search: string; setSearch: (s: string) => void;
  onRefresh: () => void; refreshing: boolean; onRemove: () => void;
}) {
  const view = (params.get('view') as View | null) ?? (c.common ? 'cheaper' : 'all');
  const sort = params.get('sort') ?? 'diff';
  const page = Number(params.get('page') ?? 1);
  const pageSize = Number(params.get('size') ?? 50);
  const q = new URLSearchParams({ view, sort, page: String(page), pageSize: String(pageSize) });
  if (params.get('q')) q.set('q', params.get('q')!);
  const items = useQuery({
    queryKey: ['meli', 'competitor-items', c.id, q.toString(), c.refreshedAt],
    queryFn: () => api<ItemsPage>(`/channels/mercadolivre/competitors/${c.id}/items?${q}`),
    placeholderData: keepPreviousData,
  });
  const [exporting, setExporting] = useState(false);
  const rows = items.data?.rows ?? [];
  const busy = busyOf(c);

  const exportAll = async () => {
    setExporting(true);
    try {
      const all = new URLSearchParams(q); all.set('page', '1'); all.set('pageSize', '5000');
      const d = await api<ItemsPage>(`/channels/mercadolivre/competitors/${c.id}/items?${all}`);
      downloadCsv(`concorrente_${(c.nickname ?? c.sellerId).toString().replace(/\W+/g, '_')}_${view}`,
        ['Anúncio dele', 'Título', 'Marca', 'EAN', 'Catálogo', 'Full', 'Frete grátis', 'Preço dele', 'Preço anterior', 'Mudou em', 'Meu anúncio', 'Meu título', 'Meu preço', 'Diferença %', 'Cruzado por', 'Link'],
        d.rows.map((r) => [r.itemId, r.title, r.brand, r.gtin, r.catalogProductId, r.full ? 'Sim' : 'Não', r.freeShipping ? 'Sim' : 'Não',
          r.price, r.prevPrice, r.priceChangedAt?.slice(0, 10), r.myItem, r.myTitle, r.myPrice, r.diff == null ? null : Math.round(r.diff * 1000) / 10,
          r.matchBy === 'catalog' ? 'catálogo' : r.matchBy === 'gtin' ? 'EAN' : null, r.permalink]));
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="stack-y">
      <div className="panel" style={{ padding: 14, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          <b>{c.nickname ?? `Vendedor ${c.sellerId}`}</b>
          {c.permalink && <> · <a className="ext" href={c.permalink} target="_blank" rel="noreferrer">perfil <ExternalLink size={11} /></a></>}
          <div className="small muted">
            {busy ? <>Lendo agora{c.progress?.phase ? `: ${PHASE[c.progress.phase] ?? c.progress.phase}` : ''}{c.progress?.read ? ` (${int(c.progress.read)}${c.progress.total ? ` de ${int(c.progress.total)}` : ''})` : ''}…</>
              : c.status === 'failed' ? <span style={{ color: 'var(--err)' }}>Última leitura falhou: {c.lastError}</span>
              : <>Atualizado {dateTime(c.refreshedAt)} · {int(c.fullItems)} no Full · {int(c.changed7d)} mudaram de preço na semana</>}
          </div>
          {c.coverageNote && <div className="small" style={{ color: 'var(--warn)', marginTop: 4 }}>{c.coverageNote}</div>}
        </div>
        <button className="btn sm" type="button" disabled={busy || refreshing} onClick={onRefresh}><RefreshCw size={14} /> Atualizar agora</button>
        <button className="btn sm quiet" type="button" onClick={onRemove}><Trash2 size={14} /> Remover</button>
      </div>

      {items.data && (
        <div className="kpis">
          {VIEWS.map((v) => (
            <button key={v.key} type="button" className={`kpi${view === v.key ? ' active' : ''}`} style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
              onClick={() => set({ view: v.key, page: null })}>
              <span className="k-label">{v.label}</span>
              <span className="k-value" style={v.key === 'cheaper' && items.data!.counts.cheaper ? { color: 'var(--err)' } : undefined}>{int(items.data!.counts[v.key] ?? 0)}</span>
              <span className="k-note">{v.note}</span>
            </button>
          ))}
        </div>
      )}

      <div className="toolbar">
        <label className="search">
          <Search size={16} />
          <span className="sr-only">Buscar</span>
          <input className="input" type="search" placeholder="Buscar por nome, marca, EAN ou MLB" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <select className="select" aria-label="Ordem" value={sort} onChange={(e) => set({ sort: e.target.value === 'diff' ? null : e.target.value, page: null })}>
          <option value="diff">Maior vantagem dele primeiro</option>
          <option value="changed">Mudança de preço mais recente</option>
          <option value="price">Maior preço</option>
          <option value="title">Nome</option>
        </select>
        <select className="select" aria-label="Por página" value={pageSize} onChange={(e) => set({ size: e.target.value === '50' ? null : e.target.value, page: null })}>
          {[50, 100, 200].map((n) => <option key={n} value={n}>{n} por página</option>)}
        </select>
        <button className="btn" type="button" disabled={exporting || !items.data?.total} onClick={exportAll}><Download size={15} /> {exporting ? 'Exportando…' : 'Exportar'}</button>
      </div>

      <div className="panel flush">
        {items.error ? <div style={{ padding: 18 }}><ErrorBox error={items.error} /></div>
          : !items.isPending && rows.length === 0 ? (
            <div className="empty">
              <b>{busy ? 'Lendo os anúncios…' : 'Nada por aqui'}</b>
              {busy ? 'A primeira leitura leva de 1 a 10 minutos, conforme o tamanho da loja.' : view === 'cheaper' ? 'Ele não está mais barato em nenhum produto que vocês dois vendem.' : 'Mude a busca ou o filtro.'}
            </div>
          ) : (
            <>
              <div className="grid-wrap" style={{ opacity: items.isPlaceholderData ? 0.55 : 1 }}>
                <table className="grid" style={{ width: '100%', minWidth: 980 }}>
                  <colgroup>{COLS.map(([, w], i) => <col key={i} style={w ? { width: w } : undefined} />)}</colgroup>
                  <thead><tr>{COLS.map(([l, , cl], i) => <th key={i} className={cl}>{l}</th>)}</tr></thead>
                  <tbody>
                    {items.isPending
                      ? Array.from({ length: 8 }, (_, i) => <tr key={i}>{COLS.map((_, j) => <td key={j}><div className="skeleton" /></td>)}</tr>)
                      : rows.map((r) => (
                          <tr key={r.itemId} style={{ cursor: 'default' }}>
                            <td><Thumb src={r.thumbnail} alt="" /></td>
                            <td>
                              <div className="cell-2">
                                <span>{r.title}</span>
                                <span className="sub">
                                  {r.permalink ? <a className="ext" href={r.permalink} target="_blank" rel="noreferrer">{r.itemId} <ExternalLink size={11} /></a> : r.itemId}
                                  {r.brand && <> · {r.brand}</>}{r.gtin && <> · {r.gtin.replace(/^0+(?=\d{8,13}$)/, '')}</>}
                                  {r.catalogListing && <> · <Tag tone="info">catálogo</Tag></>}
                                  {r.full && <> · <Tag tone="ok">Full</Tag></>}
                                  {r.freeShipping && <> · frete grátis</>}
                                </span>
                              </div>
                            </td>
                            <td className="num">
                              <b>{brl(r.price)}</b>
                              {r.prevPrice != null && r.price != null && r.prevPrice !== r.price && (
                                <div className="small muted" title={`Mudou em ${dateTime(r.priceChangedAt)}`}>
                                  {r.price < r.prevPrice ? <ArrowDown size={11} style={{ color: 'var(--err)' }} /> : <ArrowUp size={11} style={{ color: 'var(--ok)' }} />} era {brl(r.prevPrice)}
                                </div>
                              )}
                            </td>
                            <td>
                              {r.myItem ? (
                                <div className="cell-2">
                                  <span>{r.myTitle}</span>
                                  <span className="sub">
                                    {r.myPermalink ? <a className="ext" href={r.myPermalink} target="_blank" rel="noreferrer">{r.myItem} <ExternalLink size={11} /></a> : r.myItem}
                                    {' · '}pelo {r.matchBy === 'catalog' ? 'catálogo' : 'EAN'}
                                  </span>
                                </div>
                              ) : <span className="small muted">você não anuncia</span>}
                            </td>
                            <td className="num">{brl(r.myPrice)}</td>
                            <td className="num">
                              {r.diff == null ? '—' : (
                                <b style={{ color: r.diff < -0.0001 ? 'var(--err)' : r.diff > 0.0001 ? 'var(--ok)' : undefined }}>{r.diff > 0 ? '+' : ''}{pct(r.diff)}</b>
                              )}
                            </td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
              <div className="cards">
                {rows.map((r) => (
                  <div key={r.itemId} className="card-row">
                    <Thumb src={r.thumbnail} alt="" />
                    <div style={{ minWidth: 0 }}>
                      <div className="t">{r.title}</div>
                      <div className="m">{r.myItem ? <>Você: {brl(r.myPrice)}</> : 'você não anuncia'}{r.full && ' · Full'}</div>
                    </div>
                    <div className="r">
                      {brl(r.price)}
                      {r.diff != null && <div className="small" style={{ color: r.diff < 0 ? 'var(--err)' : 'var(--ok)' }}>{r.diff > 0 ? '+' : ''}{pct(r.diff)}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        {items.data && <Pager total={items.data.total} page={page} pageSize={items.data.pageSize} onPage={(p) => set({ page: p > 1 ? String(p) : null })} />}
      </div>
    </section>
  );
}
